/* global React, Icon, Button, t, toast */
// ============================================================
// INVITASJON TIL Å BLI MEDARBEIDER I ET BYRÅ
// ============================================================
// Byråinvitasjonen hadde backend fra dag én, men ingen vei inn i appen: e-posten
// bar en `?firmInvite=`-parameter som ingenting leste, og en invitert som allerede
// hadde et foretak fra før så aldri at invitasjonen fantes. Uten firmamedlemskap
// vises heller ikke byråflaten, så personen satt fast i kundeappen sin.
//
// Denne porten er de to tingene som manglet: å ta vare på lenken over en
// innlogging, og å vise invitasjonen for en bruker som allerede er logget inn.
//
// Merk hva den IKKE er: en tilgang til et klientregnskap. Å bli med i byrået gir
// bare medlemskap i byrået. Klienttilgang oppstår først når byrået setter deg på et
// oppdrag klienten har akseptert — se seksjon 3.2 i backlog-dokumentet.

const FIRM_INVITE_KEY = 'plumme-pending-firm-invite';

// Samme mønster som PlummeBusinessInvite: parameteren fanges ved lasting og fjernes
// fra adressen, slik at den overlever registrering, e-postbekreftelse og innlogging
// uten å ligge synlig i URL-en resten av økten.
const PlummeFirmInvite = {
  capture() {
    const params = new URLSearchParams(window.location.search || '');
    const inviteId = String(params.get('firmInvite') || '').trim();
    if (inviteId) {
      localStorage.setItem(FIRM_INVITE_KEY, inviteId);
      params.delete('firmInvite');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`);
    }
    return inviteId || localStorage.getItem(FIRM_INVITE_KEY) || '';
  },
  current() { return localStorage.getItem(FIRM_INVITE_KEY) || ''; },
  clear(inviteId) {
    if (!inviteId || this.current() === inviteId) localStorage.removeItem(FIRM_INVITE_KEY);
  },
};

PlummeFirmInvite.capture();

function FirmInviteGate({ backend }) {
  const [invites, setInvites] = React.useState([]);
  const [busy, setBusy] = React.useState('');
  // Backend krever bekreftet e-post: der adressen ER autorisasjonen, holder det
  // ellers å registrere seg med en adresse man ikke eier. Uten en vei videre her
  // ville brukeren bare fått en feilmelding og stått fast.
  const [needsEmailVerification, setNeedsEmailVerification] = React.useState(false);
  const userKey = backend?.user?.uid || '';

  const load = React.useCallback(async () => {
    if (!userKey) {
      setInvites([]);
      return;
    }
    try {
      // Listet gjennom samme Function som byrålista. Invitasjonen leses ikke direkte
      // fra Firestore fordi reglene sammenlikner med adressen slik den står i
      // tokenet, mens invitasjonen lagres normalisert.
      const payload = await window.PlummeBackend?.listAccountingFirmAccess?.();
      setInvites(payload?.invites || []);
    } catch (error) {
      setInvites([]);
    }
  }, [userKey]);

  React.useEffect(() => { void load(); }, [load]);

  if (!backend?.user || invites.length === 0) return null;
  // Lenken fra e-posten peker på én bestemt invitasjon. Er den fortsatt gyldig, er
  // det den som skal vises — ellers den eldste ventende.
  const linked = PlummeFirmInvite.current();
  const invite = invites.find((entry) => entry.inviteId === linked) || invites[0];
  const titleLabel = window.firmTitleLabel ? window.firmTitleLabel(invite.title) : '';

  const accept = async () => {
    setBusy('accept');
    try {
      await window.PlummeBackend?.acceptAccountingFirmInvite?.(invite.inviteId);
      PlummeFirmInvite.clear(invite.inviteId);
      setNeedsEmailVerification(false);
      toast(
        t('fi.accepted', 'Du er nå medarbeider i {firm}.').replace('{firm}', invite.firmName || t('fi.theFirm', 'byrået')),
        'success',
      );
      await load();
      // Rett inn i byråflaten. Å bli med i et byrå og så bli stående i kundeappen
      // sin ville vært å svare på invitasjonen uten å åpne døra.
      window.dispatchEvent(new CustomEvent('plumme-open-firm-surface'));
    } catch (error) {
      if (error?.domainCode === 'auth.emailNotVerified') {
        setNeedsEmailVerification(true);
        await window.PlummeBackend?.resendEmailVerification?.().catch(() => {});
      }
      toast(error?.message || t('fi.acceptFailed', 'Kunne ikke godta invitasjonen.'), 'error');
    } finally {
      setBusy('');
    }
  };

  const recheckEmailVerification = async () => {
    setBusy('verify');
    try {
      const verified = await window.PlummeBackend?.reloadEmailVerification?.();
      if (verified) {
        setNeedsEmailVerification(false);
        setBusy('');
        await accept();
        return;
      }
      toast(t('fi.stillUnverified', 'Adressen er fortsatt ikke bekreftet. Sjekk innboksen og søppelpost.'), 'error');
    } finally {
      setBusy('');
    }
  };

  const decline = async () => {
    setBusy('decline');
    try {
      await window.PlummeBackend?.declineAccountingFirmInvite?.(invite.inviteId);
      PlummeFirmInvite.clear(invite.inviteId);
      toast(t('fi.declined', 'Invitasjonen er avslått.'), 'success');
      await load();
    } catch (error) {
      toast(error?.message || String(error), 'error');
    } finally {
      setBusy('');
    }
  };

  return <aside className="household-invite-global" role="status">
    <span className="household-invite-global__icon"><Icon name="users" size={20} /></span>
    <div className="grow">
      <div className="t-h3">{t('fi.title', 'Du er invitert som medarbeider')}</div>
      <div className="t-sm muted">
        {invite.firmName || t('fi.theFirm', 'byrået')}
        {titleLabel && titleLabel !== '—' ? ' · ' + titleLabel : ''}
      </div>
      <div className="t-xs muted" style={{ fontWeight: 600, marginTop: 4 }}>
        {needsEmailVerification
          ? t('fi.verifyFirst', 'Bekreft e-postadressen din først. Vi har sendt en ny bekreftelseslenke.')
          : t('fi.body', 'Invitasjonen gir deg tilgang til selve byrået. Tilgang til den enkelte klienten får du først når byrået setter deg på et oppdrag.')}
      </div>
    </div>
    <div className="row gap-8">
      <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => void decline()}>
        {t('fi.decline', 'Avslå')}
      </Button>
      {needsEmailVerification
        ? <Button size="sm" disabled={!!busy} onClick={() => void recheckEmailVerification()}>
          {t('fi.recheck', 'Jeg har bekreftet')}
        </Button>
        : <Button size="sm" disabled={!!busy} onClick={() => void accept()}>
          {busy === 'accept' ? t('fi.accepting', 'Blir med …') : t('fi.accept', 'Bli med')}
        </Button>}
    </div>
  </aside>;
}

Object.assign(window, { FirmInviteGate, PlummeFirmInvite });
